const sanitizeIdentifierSegment = (value?: string | null): string | null => {
  if (!value || typeof value !== 'string') {
    return null;
  }

  const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return cleaned.length > 0 ? cleaned : null;
};

export const generateBuyProgramSerialNumber = (
  lin?: string | null,
  catalogIdentifier?: string | null,
): string => {
  const linSegment = sanitizeIdentifierSegment(lin);
  const catalogSegment = sanitizeIdentifierSegment(
    catalogIdentifier?.split('-').slice(1, 2).join('') ?? null,
  );
  const prefix = linSegment ?? catalogSegment ?? 'BP';

  const randomSegment =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? (crypto.randomUUID().split('-')[0]?.toUpperCase() ?? 'BPASSET')
      : Math.random().toString(36).slice(2, 10).toUpperCase();

  return `BP-${prefix}-${randomSegment}`;
};

const pruneUndefinedValues = (data: Record<string, unknown>): void => {
  Object.keys(data).forEach((key) => {
    if (data[key] === undefined) {
      delete data[key];
    }
  });
};

const resolveCatalogLin = (
  data: Record<string, unknown>,
  catalogIdentifier?: string | null,
): string | null => {
  const rawLin = data['lin'];
  if (typeof rawLin === 'string' && rawLin.trim().length > 0) {
    const normalized = rawLin.trim().toUpperCase();
    data['lin'] = normalized;
    return normalized;
  }

  if (!catalogIdentifier) {
    return null;
  }

  const segments = catalogIdentifier.split('-');
  if (segments.length > 1 && segments[1]) {
    const derived = segments[1].trim().toUpperCase();
    if (derived.length > 0) {
      data['lin'] = derived;
      return derived;
    }
  }

  return null;
};

export interface PrepareBuyProgramAssetDataParams {
  catalogId: string;
  catalogIdentifier?: string | null;
  sourceData: Record<string, unknown>;
  /** Actual price paid per unit (for Buy Program reimbursement) */
  actualPrice?: number;
  /** Tax paid per unit */
  tax?: number;
  /** Shipping cost per unit */
  shipping?: number;
}

export const prepareBuyProgramAssetData = ({
  catalogId,
  catalogIdentifier,
  sourceData,
  actualPrice,
  tax,
  shipping,
}: PrepareBuyProgramAssetDataParams): Record<string, unknown> => {
  const assetData: Record<string, unknown> = { ...sourceData };

  if ('type' in assetData) {
    delete assetData['type'];
  }

  if ('name' in assetData) {
    delete assetData['name'];
  }

  // Strip catalog-only flags that must not propagate to asset entity_data
  delete assetData['soc_tag'];

  assetData['catalog_id'] = catalogId;

  if (typeof catalogIdentifier === 'string' && catalogIdentifier.trim().length > 0) {
    assetData['catalog_entity_identifier'] = catalogIdentifier;
  } else {
    delete assetData['catalog_entity_identifier'];
  }

  const linValue = resolveCatalogLin(assetData, catalogIdentifier);
  if (!linValue) {
    delete assetData['lin'];
  }

  const rawNsn = assetData['nsn'];
  if (typeof rawNsn === 'string') {
    const trimmedNsn = rawNsn.trim();
    if (trimmedNsn.length > 0) {
      assetData['nsn'] = trimmedNsn;
    } else {
      delete assetData['nsn'];
    }
  }

  const rawUnitPrice = assetData['unit_price'];
  if (rawUnitPrice !== undefined && rawUnitPrice !== null) {
    const normalizedUnitPrice =
      typeof rawUnitPrice === 'number'
        ? rawUnitPrice
        : typeof rawUnitPrice === 'string'
          ? Number.parseFloat(rawUnitPrice)
          : null;

    if (normalizedUnitPrice !== null && Number.isFinite(normalizedUnitPrice)) {
      assetData['unit_price'] = normalizedUnitPrice;
    } else {
      delete assetData['unit_price'];
    }
  }

  // Store Buy Program pricing data for auditing and document regeneration
  if (typeof actualPrice === 'number' && Number.isFinite(actualPrice)) {
    assetData['actual_price'] = actualPrice;
  }
  if (typeof tax === 'number' && Number.isFinite(tax)) {
    assetData['tax'] = tax;
  }
  if (typeof shipping === 'number' && Number.isFinite(shipping)) {
    assetData['shipping'] = shipping;
  }

  const hasSerialNumber =
    typeof assetData['serial_number'] === 'string' &&
    (assetData['serial_number'] as string).trim().length > 0;

  if (!hasSerialNumber) {
    assetData['serial_number'] = generateBuyProgramSerialNumber(
      linValue,
      catalogIdentifier ?? null,
    );
  }

  pruneUndefinedValues(assetData);

  return assetData;
};
